import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import PlusHome from '@cont/PlusHome/PlusHome';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { fetchTopologyInfo } from '@actions/topologies';
import { fetchPartPaymentStatus } from '@actions/checkout';
import SEO from '@seo/index';
import goalSchema from '@seo/goal/goal';
import { FREE_HOME_PAGE, GOAL_PAGE } from '@constants/segment';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import GTPMobileBlocker from '@cont/GTP/GTPMobileBlocker';
import Layout from '@layout/index';
import fetchIsGoalGTP from '@api/fetchers/constants';
import { getCheckPartPaymentStatus } from '@api/selectors/auth';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { SWRConfig } from 'swr';
import { fetchGoalFAQs, fetchGoalInfo } from '@api/fetchers/goal';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import fetchGTPStaticData from '@api/fetchers/gtp';
import { useIsMobile, usePageSeoContent } from '@stores/AppCommonData';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import useFullBrowseInfo from '@api/hooks/browse/useFullBrowseInfo';
import fetchHomeFeedRowData from '@api/fetchers/browse';
import { getKey } from '@api/hooks/browse/useHomeFeed';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import { useSetSelectedNav } from '@stores/sidenav';

const BrowsePage = ({
  goalUID,
  querySlug,
  isGTP,
  // Pre-fetched data from the server
  fallbackData
}) => {
  const { isIconicAvailable, isK12Goal: isK12 } = fallbackData?.goalInfo;
  const data = useFullBrowseInfo({
    goalUID,
    fallbackData,
    isIconicAvailable,
    isK12,
    isGTP
  });

  const { goalInfo, faqs, keys } = data;

  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn || false);
  const metaInfo = usePageSeoContent();
  const { accessControl } = useAccessControl(goalUID);
  const checkPartPaymentStatus = getCheckPartPaymentStatus(accessControl);

  const dispatch = useDispatchToStore();
  const isMobile = useIsMobile();
  const router = useRouter();
  const {
    slug: goalSlug,
    title,
    uid,
    ogImage
  } = goalInfo || fallbackData?.goalInfo;
  useEffect(() => {
    dispatch(fetchTopologyInfo(goalUID));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSetSelectedNav(plusNavBarItems.BROWSE);

  useEffect(() => {
    if (querySlug !== goalSlug) {
      router.replace(
        '/goal/[goalSlug]/[goalUID]/browse',
        `/goal/${goalSlug}/${uid}/browse`,
        { shallow: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoggedIn && checkPartPaymentStatus)
      dispatch(fetchPartPaymentStatus(goalUID));
  }, [isLoggedIn, dispatch, goalUID, checkPartPaymentStatus]);

  if (isGTP && isMobile) {
    return <GTPMobileBlocker goalInfo={goalInfo || {}} />;
  }
  const homeFeedKey = getKey(goalUID);

  const goalTitle = metaInfo.title || `${title} | Unacademy`;
  const goalDescription =
    metaInfo.description ||
    `Prepare live from Top Educators with comprehensive, structured and crash courses along with practice section for ${title}  on Unacademy Plus.`;
  const url = `https://unacademy.com/goal/${goalSlug}/${uid}/browse`;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';
  return (
    <SWRConfig
      value={{
        dedupingInterval: 60 * 1000,
        fallback: {
          [keys.goalInfoSWRKey]: fallbackData.goalInfo,
          [keys.gtpStaticDataSWRKey]: fallbackData.gtpStaticContent,
          [homeFeedKey]: fallbackData.homeFeed
        }
      }}
    >
      <SEO
        title={goalTitle}
        url={url}
        description={goalDescription}
        openGraph={{
          title: goalTitle,
          description: goalDescription,
          images: [
            {
              url: imageURL
            }
          ],
          url
        }}
        schema={goalSchema(faqs, title, url, [], goalDescription)}
      />
      <PlusHome />
    </SWRConfig>
  );
};

BrowsePage.pageInfo = {
  isMWebSupported: true,
  name: GOAL_PAGE,
  layoutOptions: {
    showPlusNav: true
  }
};

export default BrowsePage;

export const getStaticProps = async ({ params: query }) => {
  const { goalUID: routeGoalUID = '', goalSlug: querySlug = '-' } = query;
  try {
    const goalUID = routeGoalUID.toUpperCase();
    const [goal, isGTP] = await Promise.all([
      fetchGoalInfo(goalUID),
      fetchIsGoalGTP(goalUID)
    ]);

    if (!goal || goal.error || goal.errorCode)
      return { notFound: true, revalidate: 60 };

    const promises = [];
    const { isK12Goal: isK12, isPlusAvailable, isPlatformGoal } = goal;

    if (isPlatformGoal)
      return {
        redirect: {
          destination: `/goal/${goal.slug || '-'}/${goalUID}`,
          permanent: true
        }
      };

    if (!isPlusAvailable && !isGTP)
      return {
        redirect: {
          destination: `/goal/${goal.slug || '-'}/${goalUID}/free-platform`,
          permanent: true
        }
      };

    if (isGTP) {
      promises.push(
        resolvePromiseToObject(
          fetchGTPStaticData(goalUID),
          'gtpStaticContent',
          true
        )
      );
    }

    promises.push(
      resolvePromiseToObject(fetchHomeFeedRowData(goalUID), 'homeFeed')
    );

    promises.push(
      resolvePromiseToObject(
        fetchPageMetaInfo(FREE_HOME_PAGE, goalUID),
        'metaInfo'
      )
    );

    promises.push(resolvePromiseToObject(fetchGoalFAQs(), 'faqs'));

    let fallbackData = {};
    if (promises.length > 0)
      fallbackData = await Promise.all(promises).then((resolvedVals) => {
        const resultObj = {};
        for (let i = 0; i < resolvedVals.length; i += 1)
          resultObj[resolvedVals[i].type] = resolvedVals[i].result;
        return resultObj;
      });
    fallbackData.goalInfo = goal;

    return {
      props: {
        goalUID,
        querySlug,
        isK12,
        isGTP,
        pageMetaInfo: fallbackData.metaInfo,
        fallbackData,
        globalFallbackData: {
          [getTopologyKey(goal.uid)]: goal
        }
      },
      revalidate: 5 * 60
    };
  } catch (error) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

BrowsePage.propTypes = {
  goalUID: PropTypes.string.isRequired,
  querySlug: PropTypes.string.isRequired
};
BrowsePage.Layout = Layout;
BrowsePage.Wrapper = PageReduxProvider;
